/**
 * The relative-duration timeline axis.
 *
 * This gantt does not show calendar dates. The axis counts outward from a `now`
 * line at zero: +1h … +23h, then 1d, then months, then years. The domain is
 * therefore "hours from now", not "wall-clock time", and the whole chart drifts
 * left on its own as now advances — which is the point: a task creeping toward
 * the now-line is something you can see.
 *
 * Pure and dependency-free so it tests under node:test with no DOM.
 */
import { HOUR_MS } from "./types.js";

export const DAY_H = 24;
// The calendar is flat by decree (every hour equal, no weekends, no working
// hours), so a month and a year are fixed hour counts rather than calendar
// steps. That keeps ticks evenly spaced, at the cost of "+3m" landing 91.3 days
// out rather than on a month boundary. Even spacing is the right trade for an
// axis whose labels mean "about three months out".
export const MONTH_H = 730.485; // 365.2425 * 24 / 12
export const YEAR_H = 8765.82; // 365.2425 * 24

export type TickUnit = "h" | "d" | "m" | "y";

export interface LadderStep {
  unit: TickUnit;
  n: number;
  hours: number;
}

export const TICK_LADDER: readonly LadderStep[] = [
  { unit: "h", n: 1, hours: 1 },
  { unit: "h", n: 2, hours: 2 },
  { unit: "h", n: 3, hours: 3 },
  { unit: "h", n: 6, hours: 6 },
  { unit: "h", n: 12, hours: 12 },
  { unit: "d", n: 1, hours: DAY_H },
  { unit: "d", n: 2, hours: 2 * DAY_H },
  { unit: "d", n: 5, hours: 5 * DAY_H },
  { unit: "d", n: 10, hours: 10 * DAY_H },
  { unit: "m", n: 1, hours: MONTH_H },
  { unit: "m", n: 2, hours: 2 * MONTH_H },
  { unit: "m", n: 3, hours: 3 * MONTH_H },
  { unit: "m", n: 6, hours: 6 * MONTH_H },
  { unit: "y", n: 1, hours: YEAR_H },
  { unit: "y", n: 2, hours: 2 * YEAR_H },
  { unit: "y", n: 5, hours: 5 * YEAR_H },
  { unit: "y", n: 10, hours: 10 * YEAR_H },
];

/** Zoom bounds: ~4 minutes per pixel of detail, out to a decade on screen. */
export const MIN_PX_PER_HOUR = 0.0004;
export const MAX_PX_PER_HOUR = 240;

export interface Tick {
  /** Offset from now, in hours. Zero is the now-line. */
  hours: number;
  x: number;
  label: string;
  unit: TickUnit;
  /** The real timestamp behind the label. */
  absMs: number;
}

export interface AxisTicks {
  step: LadderStep;
  major: Tick[];
  minor: Tick[];
}

/** "now" | "+6h" | "-2d" | "+3m" | "+1y" */
export function formatOffset(hours: number, unit: TickUnit): string {
  if (Math.abs(hours) < 1e-9) return "now";
  const sign = hours < 0 ? "-" : "+";
  const a = Math.abs(hours);
  switch (unit) {
    case "h":
      return `${sign}${Math.round(a)}h`;
    case "d":
      return `${sign}${Math.round(a / DAY_H)}d`;
    case "m":
      return `${sign}${Math.round(a / MONTH_H)}m`;
    case "y":
      return `${sign}${Math.round(a / YEAR_H)}y`;
  }
}

export interface AxisOptions {
  /** Viewport, in hours from now. */
  h0: number;
  h1: number;
  pxPerHour: number;
  nowMs: number;
  /** Narrowest a label like "+12h" reads at. */
  minLabelPx?: number;
  /** Below this, gridlines stop being information and become texture. */
  minGridPx?: number;
}

/**
 * Ticks are anchored at ZERO, never at the viewport edge. That is what makes
 * this axis behave: the now-line always coincides exactly with a labelled tick,
 * and panning slides existing ticks through the viewport instead of resampling
 * them — so a label never jitters by a few minutes as you scroll.
 */
export function axisTicks(o: AxisOptions): AxisTicks {
  const minLabelPx = o.minLabelPx ?? 72;
  const minGridPx = o.minGridPx ?? 9;

  const found = TICK_LADDER.findIndex((s) => s.hours * o.pxPerHour >= minLabelPx);
  const index = found === -1 ? TICK_LADDER.length - 1 : found;
  const step = TICK_LADDER[index]!;

  const build = (spacing: number, labelled: boolean): Tick[] => {
    const out: Tick[] = [];
    // Guard against a spacing so small relative to the span that we would
    // generate millions of ticks for a viewport nobody can read anyway.
    if ((o.h1 - o.h0) / spacing > 10_000) return out;
    const first = Math.ceil(o.h0 / spacing - 1e-9) * spacing;
    for (let h = first; h <= o.h1 + 1e-9; h += spacing) {
      const hours = Math.abs(h) < 1e-9 ? 0 : h;
      out.push({
        hours,
        x: (hours - o.h0) * o.pxPerHour,
        label: labelled ? formatOffset(hours, step.unit) : "",
        unit: step.unit,
        absMs: o.nowMs + hours * HOUR_MS,
      });
    }
    return out;
  };

  const major = build(step.hours, true);

  // Minor gridlines one rung down — or the flat 1h grid, which is what makes a
  // 6h task visibly span exactly six columns.
  const sub = index > 0 ? TICK_LADDER[index - 1]! : { ...step, hours: 1 };
  const minor =
    sub.hours < step.hours && sub.hours * o.pxPerHour >= minGridPx
      ? build(sub.hours, false).filter((t) => Math.abs(t.hours % step.hours) > 1e-9)
      : [];

  return { step, major, minor };
}

/** Convert a timestamp to hours-from-now, and back. */
export function msToHours(ms: number, nowMs: number): number {
  return (ms - nowMs) / HOUR_MS;
}

export function hoursToMs(hours: number, nowMs: number): number {
  return nowMs + hours * HOUR_MS;
}

/** Zoom about a fixed point, so the hour under the cursor stays under it. */
export function zoomAbout(o: {
  h0: number;
  pxPerHour: number;
  cursorX: number;
  factor: number;
}): { h0: number; pxPerHour: number } {
  const pxPerHour = Math.min(MAX_PX_PER_HOUR, Math.max(MIN_PX_PER_HOUR, o.pxPerHour * o.factor));
  const hAtCursor = o.h0 + o.cursorX / o.pxPerHour;
  return { h0: hAtCursor - o.cursorX / pxPerHour, pxPerHour };
}
